import { useRef } from 'react'
import { Plus } from 'lucide-react'
import PlayCard from '../playbook-editor/PlayCard'
import { useDelayedUnmount } from '@/hooks'
import type { Drawing } from '@/types'
import type { Player } from '@/types/Player.types'
import './editor-cards-panel.css'

const ANIMATION_DURATION = 800 // ms - must match CSS transition duration

interface EditorCardItem {
	id: string
	name: string
	formation?: string
	playType?: string
	defensiveFormation?: string
	drawings?: Drawing[]
	players?: Player[]
	personnel?: string
	labelObjects?: { id: string; name: string; color: string }[]
	thumbnail?: string | null
	linkedFormationId?: string | null
	linkedFormationName?: string | null
}

interface EditorCardsPanelProps {
	items: EditorCardItem[]
	cardType?: 'play' | 'formation'
	currentItemId?: string
	showPanel: boolean
	onOpen: (id: string) => void
	onAdd: () => void
	isAdding?: boolean
	addLabel?: string
	onRename?: (id: string) => void
	onDelete?: (id: string) => void
	onDuplicate?: (id: string) => void
	onAnimate?: (id: string) => void
	onUnlink?: (id: string) => void
}

const NOOP = () => {}

export function EditorCardsPanel({
	items,
	cardType = 'play',
	currentItemId,
	showPanel,
	onOpen,
	onAdd,
	isAdding,
	addLabel,
	onRename,
	onDelete,
	onDuplicate,
	onAnimate,
	onUnlink,
}: EditorCardsPanelProps) {
	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const shouldRenderContent = useDelayedUnmount(showPanel, ANIMATION_DURATION)

	const defaultAddLabel = cardType === 'formation' ? 'Add Formation' : 'Add Play'
	const resolvedAddLabel = addLabel ?? defaultAddLabel

	return (
		<div
			className="editor-cards-panel"
			style={{
				width: showPanel ? '360px' : '0px',
			}}
		>
			{shouldRenderContent && (
				<div className="editor-cards-panel__content">
					<div
						ref={scrollContainerRef}
						className="editor-cards-panel__scroll"
					>
						{items
							.filter((item) => item.id !== currentItemId)
							.map((item) => (
								<div key={item.id} className="editor-cards-panel__card-wrapper">
									<PlayCard
										id={item.id}
										name={item.name}
										formation={item.formation ?? ''}
										playType={item.playType ?? ''}
										defensiveFormation={item.defensiveFormation ?? ''}
										drawings={item.drawings}
										players={item.players}
										personnel={item.personnel}
										labelObjects={item.labelObjects}
										thumbnail={item.thumbnail ?? undefined}
										linkedFormationId={item.linkedFormationId ?? undefined}
										linkedFormationName={item.linkedFormationName ?? undefined}
										cardType={cardType}
										onOpen={onOpen}
										onRename={onRename ?? NOOP}
										onDelete={onDelete ?? NOOP}
										onDuplicate={onDuplicate ?? NOOP}
										onAnimate={onAnimate}
										onUnlink={onUnlink}
									/>
								</div>
							))}
						<button
							onClick={onAdd}
							disabled={isAdding}
							className="editor-cards-panel__add-button"
							aria-label={resolvedAddLabel}
						>
							{isAdding ? (
								<>
									<div className="editor-cards-panel__add-icon">
										<div className="editor-cards-panel__add-spinner" />
									</div>
									<span className="editor-cards-panel__add-text">Creating...</span>
								</>
							) : (
								<>
									<div className="editor-cards-panel__add-icon">
										<Plus size={24} />
									</div>
									<span className="editor-cards-panel__add-text">{resolvedAddLabel}</span>
								</>
							)}
						</button>
					</div>
				</div>
			)}
		</div>
	)
}
